import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tags } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageWrap, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ConfirmDialog } from "../components/ConfirmDialog";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#F43F5E", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16"];

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/categories").then((r) => setCats(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { name: form.name, color: form.color };
      if (form.id) await api.put(`/categories/${form.id}`, body);
      else await api.post("/categories", body);
      toast.success("Kategori disimpan");
      setForm(null);
      load();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  const remove = async () => {
    await api.delete(`/categories/${del.id}`);
    toast.success("Kategori dihapus");
    setDel(null);
    load();
  };

  return (
    <PageWrap>
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{cats.length} kategori</p>
        <Button onClick={() => setForm({ name: "", color: COLORS[0] })} data-testid="add-category-btn"><Plus className="h-4 w-4 mr-2" />Tambah Kategori</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {cats.map((c) => (
          <div key={c.id} className="rounded-2xl border bg-card p-5 flex items-center gap-4 fade-up" data-testid={`category-card-${c.id}`}>
            <div className="h-12 w-12 rounded-xl grid place-items-center text-white" style={{ background: c.color }}><Tags className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold truncate">{c.name}</div>
              <div className="text-xs text-muted-foreground">{c.product_count} produk</div>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setForm(c)} data-testid={`edit-category-${c.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDel(c)} data-testid={`delete-category-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      {cats.length === 0 && <Empty icon={Tags} text="Belum ada kategori." />}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-sm" data-testid="category-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Edit" : "Tambah"} Kategori</DialogTitle>
            <DialogDescription>Nama dan warna penanda kategori.</DialogDescription>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="space-y-1.5"><Label>Nama</Label><Input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name-input" /></div>
              <div className="space-y-1.5">
                <Label>Warna</Label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((c) => (
                    <button type="button" key={c} onClick={() => setForm({ ...form, color: c })} data-testid={`category-color-${c.slice(1)}`}
                      className={`h-8 w-8 rounded-full ring-offset-2 ring-offset-background ${form.color === c ? "ring-2 ring-foreground" : ""}`} style={{ background: c }} />
                  ))}
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="category-save-btn">Simpan</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Hapus kategori?" description="Produk dalam kategori ini akan menjadi tanpa kategori." onConfirm={remove} />
    </PageWrap>
  );
}
